import React from "react";
import { Box, Button, Typography } from "@mui/material";
import IconCircle from "../shared/IconCircle";
import {
  COLORS,
  CONTENT_MAX_WIDTH,
  FONT,
  cardDescSx,
  cardTitleSx,
} from "../shared/styles";
import { IpIcon, LastActiveIcon, LaptopIcon, LocationIcon } from "./icons";
import SessionDetail from "./SessionDetail";
import { formatLastActive, formatLocation } from "./sessionUtils";

function SessionCard({ session, isCurrent, loggingOutId, onLogout }) {
  const deviceName = session.os || session.deviceType || "Unknown device";
  const deviceDesc = session.browser || "Unknown browser";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: CONTENT_MAX_WIDTH,
        minHeight: 152,
        p: "16px",
        borderRadius: "8px",
        border: `1px solid ${isCurrent ? COLORS.primary : COLORS.border}`,
        bgcolor: isCurrent ? "#F3FBFF" : COLORS.white,
        boxShadow: isCurrent ? "0 1px 2px 0 rgba(20, 141, 253, 0.1)" : "none",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        <IconCircle bgcolor={isCurrent ? "#D6EDFF" : "#F5F7F9"}>
          <LaptopIcon color={isCurrent ? COLORS.primary : COLORS.textSecondary} />
        </IconCircle>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <Box>
              <Typography component="p" sx={cardTitleSx}>
                {deviceName}
              </Typography>
              <Typography component="p" sx={{ ...cardDescSx, mt: "2px" }}>
                {deviceDesc}
              </Typography>
            </Box>

            {isCurrent ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 24,
                  px: "10px",
                  borderRadius: "100px",
                  bgcolor: COLORS.primary,
                  color: COLORS.white,
                  fontFamily: FONT.family,
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "22px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Current Session
              </Box>
            ) : (
              <Button
                type="button"
                onClick={() => onLogout(session.sessionId)}
                disabled={loggingOutId === session.sessionId}
                disableRipple
                sx={{
                  width: 72,
                  height: 24,
                  minWidth: 72,
                  p: 0,
                  border: "1px solid #D5DCE1",
                  background: "transparent",
                  color: COLORS.error,
                  fontFamily: FONT.family,
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "22px",
                  textTransform: "none",
                  borderRadius: "8px",
                  flexShrink: 0,
                  "&:hover": { background: "transparent" },
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                {loggingOutId === session.sessionId ? "..." : "Logout"}
              </Button>
            )}
          </Box>

          <Box
            sx={{
              width: 536,
              maxWidth: "100%",
              height: "1px",
              mt: "20px",
              bgcolor: isCurrent ? "#D6EDFF" : COLORS.borderLight,
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px",
              mt: "20px",
              "@media (max-width: 768px)": {
                gridTemplateColumns: "1fr",
              },
            }}
          >
            <SessionDetail
              icon={<LocationIcon />}
              label="Location"
              value={formatLocation(session.location)}
            />
            <SessionDetail
              icon={<IpIcon />}
              label="IP Address"
              value={session.ip || "Unknown"}
            />
            <SessionDetail
              icon={<LastActiveIcon />}
              label="Last Active"
              value={formatLastActive(session.lastUsed, isCurrent)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SessionCard;
